import React from "react";
import { Container, Box } from "@mui/material";
import Grid from '@mui/material/Grid2';
import Image from "components/Image";
import Slider from "components/Slider";
import zparse from "components/zparse";
import Icon from "components/Icon";
import theme from "utils/theme";

const Gallery = ({ gallery }) => {
	const images = gallery.image;
	if (!images && images?.length === 0) return null;

	const isFullWidth = !!gallery.fullWidth;
	const layout = gallery.layout;
	const mobileLayout = gallery.mobileLayout;

	const sliderParams = {
		sliderPerView: 1,
		loop: images?.length > 1 ?? false,
		autoplay: {
			delay: 5000,
			disableOnInteraction: false
		},
		watchSlidesProgress: true,
		effect: !isFullWidth ? 'flip' : 'fade',
		...(!isFullWidth ? {} : {
			pagination: false,
			navigation: {
				prevEl: '#gallery-button-prev',
				nextEl: '#gallery-button-next',
			},
		}),
		fadeEffect: {
			crossFade: true
		},
		injectStyles: [
			`
				.swiper-pagination-bullet {
					width: 14px !important;
					height: 14px;
					border-radius: 50%;
					position: relative;
					overflow: hidden;
					background: transparent !important;
					border: 2px solid white;
					opacity: 1;
					
					&::before {
						content: "";
						display: block;
						position: absolute;
						top: 0;
						right: 0;
						bottom: 0;
						left: 0;
					}
				}

				.swiper-pagination-bullet-active {
					background: white !important;
					opacity: 1;
					
					&::before {
						background-color: white;
					}
				}

				.swiper-pagination {
					position: relative;
					top: initial !important;
					bottom: initial !important;
					pointer-events: all !important;
					display: flex;
					gap: 12px;
					justify-content: center;
					margin-top: 22px !important;
				}

				${theme.breakpoints.down('laptop')} {
					.swiper-pagination {
						gap: 0px;
					}
				}

				:host {
					--swiper-theme-color: white;
				}
			`
		],
	};

	const sliderColumnStyle = {
		position: 'relative',
		width: '100%',
		minWidth: 0,
		order: mobileLayout === 'imageFirst' ? 1 : 2,
		...(isFullWidth && { width: '100%' }),
		[theme.breakpoints.up('sm')]: {
			width: isFullWidth ? '100%' : 'calc(50% - var(--Grid-rowSpacing))',
			order: layout === 'imageFirst' ? 1 : 2,
		},
		[theme.breakpoints.down('1200')]: {
			display: 'flex',
			gap: '7px',
			justifyContent: 'center',
			alignItems: 'center',
			'.swiper-target': {
				minWidth: '320px',
				overflow: 'hidden',
				width: 'calc(100% - var(--Grid-rowSpacing))',
			},
			'.swiper-button-prev-custom, .swiper-button-next-custom': {
				position: 'static',
			}
		},
	}

	const contentColumnStyle = {
		width: '100%',
		order: mobileLayout === 'imageFirst' ? 2 : 1,
		...(isFullWidth && { width: '100%' }),
		[theme.breakpoints.up('sm')]: {
			width: isFullWidth ? '100%' : 'calc(50% - var(--Grid-rowSpacing))',
			order: layout === 'imageFirst' ? 2 : 1,
		},
	}

	return (
		<Container className="SmartTag-root Gallery-root">
			<Container>
				<Grid container>
					<Grid
						sx={sliderColumnStyle}
						size={{ xs: "12", sm: isFullWidth ? "12" : "auto" }}
					>
						{(isFullWidth && images && images?.length > 1) && (
							<Box id="gallery-button-prev" className="swiper-button-prev-custom swiper-button-prev">
								<Icon symbol="gallery-chevron-left" />
							</Box>
						)}
						<Box className="swiper-target">
							<Slider params={sliderParams} initialize={true}>
								{images && images.map((image, index) => (
									<swiper-slide key={`slide-${index}-${layout}`}>
										<Box
											sx={{
												borderRadius: '8px',
												overflow: 'hidden',
												".Image-root": {
													display: 'flex',
													justifyContent: 'center',
												},
											}}
										>
											<Image
												layout={Image.CONSTRAINED}
												src={image}
												alt=""
											/>
										</Box>
									</swiper-slide>
								))}
							</Slider>
						</Box>
						{(isFullWidth && images && images?.length > 1) && (
							<Box id="gallery-button-next" className="swiper-button-next-custom swiper-button-next">
								<Icon symbol="gallery-chevron-right" />
							</Box>
						)}
					</Grid>

					<Grid
						sx={contentColumnStyle}
						size={{ xs: "12", sm: isFullWidth ? "12" : "auto" }}
					>
						{zparse(gallery.content)}
					</Grid>
				</Grid>
			</Container>
		</Container>
	);
};

export default Gallery;
